/**
 * Hero Background Video
 *
 * Two background video sources are supported:
 *
 * 1. Self-hosted file — rendered server-side as a native <video muted loop
 *    playsinline poster="..." preload="none"> with NO <source> and NO
 *    `autoplay`, so the browser fetches zero bytes of it until this script
 *    runs and the hero scrolls near the viewport. Until then the `poster`
 *    attribute IS the image fallback — a pure-HTML, zero-JS, zero-network
 *    background that also keeps a multi-MB video file from competing with
 *    the page's actual LCP image/CSS/fonts for bandwidth on load. This
 *    module lazily attaches the real <source> (IntersectionObserver-gated,
 *    same as the YouTube path below) and adds the optional "loop before N
 *    seconds" trim.
 *
 * 2. YouTube URL — progressively enhanced in, since a YouTube iframe can't
 *    be embedded without JS. The static background image (rendered
 *    server-side) is always shown first and remains the fallback until this
 *    script actually runs and the YouTube player fires its first "playing"
 *    state — so sites with WP Rocket's "Delay JavaScript Execution" enabled
 *    simply show the image for longer, never a broken/empty hero.
 *
 *    Playback is driven by the YouTube IFrame Player API (rather than the
 *    iframe `loop`/`playlist` URL trick) so looping is a same-player
 *    seek+play instead of a full iframe reload — that avoids the
 *    black-frame flash and "video ended" overlay that the URL-based loop
 *    trick causes. Note: YouTube's player still flashes its own
 *    play/pause icon on state changes regardless of the `controls`
 *    param — that is a YouTube UI limitation with no CSS/JS workaround
 *    from a cross-origin iframe, which is why the self-hosted file option
 *    above is the recommended source when that's unacceptable.
 *
 * @package Advanced Regeneration
 */

function attachHeroNativeVideoSource(video) {
  const src = video.dataset.heroVideoSrc;
  if (!src || video.querySelector('source')) {
    return;
  }

  const source = document.createElement('source');
  source.src = src;
  source.type = 'video/mp4';
  video.appendChild(source);
  video.load();
  video.play().catch(() => {});

  const endSeconds = parseFloat(video.dataset.heroVideoEnd);
  if (Number.isFinite(endSeconds) && endSeconds > 0) {
    video.addEventListener('timeupdate', () => {
      if (video.currentTime >= endSeconds) {
        video.currentTime = 0;
        video.play().catch(() => {});
      }
    });
  }
}

function initHeroNativeVideos() {
  const videos = document.querySelectorAll('.block-hero__video--native[data-hero-video-src]');
  if (!videos.length) {
    return;
  }

  // No <source> was ever rendered, so simply doing nothing here leaves the
  // poster image as a permanent, zero-network background — exactly what we
  // want for reduced-motion users.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  if (!('IntersectionObserver' in window)) {
    videos.forEach(attachHeroNativeVideoSource);
    return;
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          attachHeroNativeVideoSource(entry.target);
          obs.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '200px 0px' }
  );

  videos.forEach((video) => observer.observe(video));
}

let youTubeApiPromise = null;

function loadYouTubeIframeApi() {
  if (youTubeApiPromise) {
    return youTubeApiPromise;
  }

  youTubeApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve(window.YT);
      return;
    }

    const previousReadyCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previousReadyCallback === 'function') {
        previousReadyCallback();
      }
      resolve(window.YT);
    };

    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(script);
  });

  return youTubeApiPromise;
}

function createHeroPlayer(bg, YT) {
  const hero = bg.closest('.block-hero');
  const videoId = bg.dataset.heroVideoId;

  if (!hero || !videoId || hero.classList.contains('is-video-loaded')) {
    return;
  }

  const endSeconds = parseInt(bg.dataset.heroVideoEnd, 10);

  const mount = document.createElement('div');
  bg.insertAdjacentElement('afterend', mount);

  const playerVars = {
    autoplay: 1,
    mute: 1,
    controls: 0,
    disablekb: 1,
    fs: 0,
    iv_load_policy: 3,
    cc_load_policy: 0,
    modestbranding: 1,
    playsinline: 1,
    rel: 0,
    origin: window.location.origin,
  };

  if (Number.isFinite(endSeconds) && endSeconds > 0) {
    playerVars.end = endSeconds;
  }

  const player = new YT.Player(mount, {
    videoId,
    host: 'https://www.youtube-nocookie.com',
    playerVars,
    events: {
      // Autoplay (via playerVars) already starts playback — calling
      // mute()/playVideo() again here would fight it and trigger a
      // visible pause/play flash icon, so onReady only tags the iframe.
      onReady: (event) => {
        const iframe = event.target.getIframe();
        if (iframe) {
          iframe.classList.add('block-hero__video');
          iframe.setAttribute('tabindex', '-1');
          iframe.setAttribute('aria-hidden', 'true');
        }
      },
      onStateChange: (event) => {
        if (event.data === YT.PlayerState.PLAYING) {
          hero.classList.add('is-video-loaded');
        } else if (event.data === YT.PlayerState.ENDED) {
          // Also reached when a configured `end` time is hit — restarts
          // the loop before the source clip's black/faded tail plays.
          event.target.seekTo(0, true);
          event.target.playVideo();
        }
      },
    },
  });

  // The iframe element exists synchronously once the constructor returns.
  const iframe = player.getIframe && player.getIframe();
  if (iframe) {
    iframe.classList.add('block-hero__video');
  }
}

export function initHeroVideoBackgrounds() {
  initHeroNativeVideos();

  const backgrounds = document.querySelectorAll('.block-hero__bg[data-hero-video-id]');
  if (!backgrounds.length) {
    return;
  }

  // Respect reduced-motion preference and skip autoplaying video entirely.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  const loadVideo = (bg) => {
    loadYouTubeIframeApi().then((YT) => createHeroPlayer(bg, YT));
  };

  if (!('IntersectionObserver' in window)) {
    backgrounds.forEach(loadVideo);
    return;
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          loadVideo(entry.target);
          obs.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '200px 0px' }
  );

  backgrounds.forEach((bg) => observer.observe(bg));
}
